import { defineStore, acceptHMRUpdate } from 'pinia';
import { GraphNodeUtil, type GNode, type GraphNode, type GraphDoc } from '../models/graphNode';
import { Firestore, collection, doc, getDoc, getDocs, setDoc, deleteDoc, query, where } from 'firebase/firestore';
import isNil from 'lodash/isNil';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import { shape2Offset, getType2SVG, type2Shape, slugify, LOCATION_DEFAULT_SHAPE, type GraphLocation } from '~/models/graphTypes';
import { type Graph, type Link } from '@livereader/graphly-d3';
import { graphToDot, parseDot, dotNodeFields, type ParsedDot, type DotNodeFields } from '~/composables/useGraphDot';

export interface JoinDialogState {
	open: boolean;
	// join      = merge the current mission under a node in another mission
	// orphan    = resolve an orphaned subtree (join / delete / new graph)
	// node      = ADD another parent to the subject node
	// setparent = REPLACE the subject node's parent(s) with the picked node
	// flip      = REVERSE the edge between the subject and a connected node
	// unjoin    = REMOVE the edge between the subject and a connected node
	mode: 'join' | 'orphan' | 'node' | 'setparent' | 'flip' | 'unjoin';
	// The node the dialog acts on (orphan root, or the node being linked).
	subjectNodeId: string;
}

export interface GraphNodeInterface {
	currentGraphNode: string;
	editingGraphNode: string;
	nodes: GNode[];
	graphs: GraphDoc[];
	currentGraphId: string;
	pendingOrphans: string[];
	joinDialog: JoinDialogState;
	// "type:subType" (or "type") -> Itemizer glyph name, the saved defaults.
	glyphDefaults: Record<string, string>;
	// "type:subType" (or "type") -> node colour, applied when the type changes.
	colorDefaults: Record<string, string>;
	// glyph name -> raw SVG text, loaded on demand from the glyph archive.
	glyphSvgCache: Record<string, string>;
	// 'focus' = centre + parent/child arcs; 'map' = full force-directed graph.
	viewMode: 'focus' | 'map';
	// Single-level undo: a snapshot of nodes+graphs taken before the last
	// structural action, plus a label for the button tooltip.
	undoSnapshot: { nodes: GNode[]; graphs: GraphDoc[]; label: string } | null;
	// User-editable location categories (types) + their sub-locations.
	locations: GraphLocation[];
}

// Debounced autosave bookkeeping (module scope; the store is a singleton).
let saveTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSaveId = '';

// The shapes TreeChartCentral registers templates for. DOT import clamps to
// these so a foreign Graphviz shape can't leave a node unrenderable.
const VALID_NODE_SHAPES = new Set([
	'circle', 'triangle', 'square', 'diamond', 'pentagon', 'hexagon', 'octagon',
]);

// Apply a parsed DOT node's fields onto a GNode, but only where the DOT
// actually carries a value: empty/absent attributes leave the existing value
// intact. That is what makes a merge safe (a lossy AI-authored DOT can't wipe
// a GM's rich-text notes) and is harmless for a fresh import (the node starts
// from defaults anyway). Edges and identity are handled by the caller.
function applyDotFields(node: GNode, f: DotNodeFields): void {
	if (f.title) node.title = f.title;
	if (f.shape) {
		// Clamp to a renderable shape; foreign Graphviz shapes (box, ellipse,
		// ...) fall back to the type default rather than failing to render.
		node.shape = VALID_NODE_SHAPES.has(f.shape) ? f.shape : type2Shape[f.type] ?? node.shape;
	}
	if (f.color) node.color = f.color;
	if (f.ident) node.ident = f.ident;
	if (f.type) node.type = f.type;
	if (f.subType) node.subType = f.subType;
	if (f.glyph) node.glyph = f.glyph;
	if (f.glyphColor) node.glyphColor = f.glyphColor;
	if (f.tags.length) node.tags = f.tags;
	if (f.info) node.info = f.info;
	if (f.readAloud) node.readAloud = f.readAloud;
	if (f.environment) node.environment = f.environment;
	if (f.images.length) node.images = f.images;
	if (f.x != null) node.x = f.x;
	if (f.y != null) node.y = f.y;
}

// ─── Shared graph-payload helpers ──────────────────────────────────────
// Used by both getCurrentGraph (focus view) and getMapGraph (full view), so
// glyph/title/colour handling stays identical across the two.

// Explicit node glyph -> subtype default -> type default -> type icon.
function resolveGlyphSvg(node: GraphNode, state: GraphNodeInterface): string {
	const name =
		node.glyph ||
		state.glyphDefaults[`${node.type}:${node.subType}`] ||
		state.glyphDefaults[node.type] ||
		'';
	if (name && state.glyphSvgCache[name]) {
		return state.glyphSvgCache[name];
	}
	return node.svgData ?? '';
}

function createGraphNode(node: GraphNode, state: GraphNodeInterface) {
	return {
		id: node.id,
		shape: {
			type: node.shape || 'circle',
			scale: 0.6,
		},
		x: 0,
		y: 0,
		payload: {
			// Room ident prefixes the displayed title ("2B Med Bay").
			title: node.ident ? `${node.ident} ${node.title}` : node.title,
			color: node.color,
			// Separate glyph colour when set, else the node colour.
			glyphColor: node.glyphColor || node.color,
			svg: resolveGlyphSvg(node, state),
			x: node.textX,
			y: node.textY,
		},
	};
}

function createGraphLink(sourceId: string, targetId: string, color: string): Link {
	return {
		source: sourceId,
		target: targetId,
		directed: true,
		strength: 'loose',
		label: '',
		curvature: 0,
		color: color,
	};
}

export const useGraphNodeStore = defineStore('GraphNodeStore', {
	state: (): GraphNodeInterface => ({
		currentGraphNode: '',
		editingGraphNode: '',
		nodes: [],
		graphs: [],
		currentGraphId: '',
		pendingOrphans: [],
		joinDialog: { open: false, mode: 'join', subjectNodeId: '' },
		glyphDefaults: {},
		colorDefaults: {},
		glyphSvgCache: {},
		viewMode: 'focus',
		undoSnapshot: null,
		locations: [],
	}),

	getters: {
		getCurrentNode: (state: GraphNodeInterface) => {
			return state.editingGraphNode;
		},
		canUndo: (state: GraphNodeInterface): boolean => state.undoSnapshot !== null,
		undoLabel: (state: GraphNodeInterface): string => state.undoSnapshot?.label ?? '',
		// Location categories as Type-dropdown options (appended to structural types).
		locationTypeOptions: (state: GraphNodeInterface): { label: string; value: string }[] =>
			state.locations.map((l) => ({ label: l.name, value: l.id })),
		// Tag cloud across every loaded node, with usage counts (most-used first).
		getTagCloud: (state: GraphNodeInterface): string[] => {
			const counts = new Map<string, number>();
			for (const node of state.nodes) {
				for (const tag of node.tags ?? []) {
					counts.set(tag, (counts.get(tag) ?? 0) + 1);
				}
			}
			return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([tag]) => tag);
		},
		getEditingNode: (state: GraphNodeInterface) => {
			const node = find(state.nodes, { id: state.editingGraphNode });
			if (node) {
				return node;
			}
			return GraphNodeUtil.defaultNode()
		},
		getCurrentGraph: (state: GraphNodeInterface) => {
			const centerNode = GraphNodeUtil.graphNodeFromNode(
				find(state.nodes, { id: state.currentGraphNode }) || GraphNodeUtil.defaultNode()
			);
			buildNodesGraph(centerNode, state);
			const graph: Graph = {
				nodes: [createGraphNode(centerNode, state)],
				links: [],
			};

			// Dedupe node ids: a 2-cycle (a node that is both parent AND child
			// of the centre) would otherwise push the same id twice, which
			// crashes graphly's render (duplicate-key enter -> getAttribute on
			// the simulation object). Links may still go both directions.
			const addedIds = new Set<string>([centerNode.id]);
			const pushNode = (node: GraphNode) => {
				if (!addedIds.has(node.id)) {
					graph.nodes.push(createGraphNode(node, state));
					addedIds.add(node.id);
				}
			};

			// Adding parent nodes and links
			centerNode.parentNodes.forEach((parent) => {
				if (parent) {
					pushNode(parent);
					graph.links.push(createGraphLink(parent.id, centerNode.id, '#00ffea'));
				}
			});

			// Adding child nodes and links
			centerNode.childNodes.forEach((child) => {
				if (child) {
					pushNode(child);
					graph.links.push(createGraphLink(centerNode.id, child.id, '#00ffea'));
				}
			});

			return graph;

			function buildNodesGraph(node: GraphNode, state: GraphNodeInterface) {
				for (const nodeId of node.parents) {
					const nodeListIndex = findIndex(state.nodes, { id: nodeId });
					if (nodeListIndex == -1) {
						console.error(`GRAPHING Unable to locate NodeID ${nodeId}`);
					} else {
						node.parentNodes.push(GraphNodeUtil.graphNodeFromNode(state.nodes[nodeListIndex]));
					}
				}

				for (const nodeId of node.children) {
					const nodeListIndex = findIndex(state.nodes, { id: nodeId });
					if (nodeListIndex == -1) {
						console.error(`GRAPHING Unable to locate NodeID ${nodeId}`);
					} else {
						node.childNodes.push(GraphNodeUtil.graphNodeFromNode(state.nodes[nodeListIndex]));
					}
				}
			}
		},
		// Full-graph Map view: every node reachable from the current graph's
		// root (cycles/diamonds included), one edge per in-set parent->child.
		// Saved positions are pinned (x/y + fx/fy); unsaved nodes are left for
		// graphly's spawn placement. loadFullGraph() must run first so every
		// reachable node is present in state.nodes.
		getMapGraph: (state: GraphNodeInterface): Graph => {
			const graphDoc = find(state.graphs, { id: state.currentGraphId });
			if (!graphDoc) {
				return { nodes: [], links: [] };
			}

			// BFS the reachable set from local state (visited Map = cycle-safe).
			const reachable = new Map<string, GraphNode>();
			const queue = [graphDoc.root];
			while (queue.length > 0) {
				const id = queue.shift() as string;
				if (reachable.has(id)) {
					continue;
				}
				const raw = find(state.nodes, { id });
				if (!raw) {
					continue;
				}
				const gn = GraphNodeUtil.graphNodeFromNode(raw);
				reachable.set(id, gn);
				queue.push(...gn.children);
			}

			const nodes = [...reachable.values()].map((node) => {
				const base = createGraphNode(node, state) as Record<string, unknown>;
				// Drop the focus x:0/y:0 seed so unsaved nodes spawn-place.
				delete base.x;
				delete base.y;
				// Pin saved positions (!== null so a saved 0 still pins).
				if (node.x !== null && node.y !== null) {
					base.x = node.x;
					base.y = node.y;
					base.fx = node.x;
					base.fy = node.y;
				}
				// Highlight the selected (editing) node in red.
				if (node.id === state.editingGraphNode) {
					(base.payload as Record<string, unknown>).color = '#ff0000';
				}
				return base;
			});

			const links: Link[] = [];
			const seen = new Set<string>();
			for (const node of reachable.values()) {
				for (const childId of node.children) {
					if (!reachable.has(childId)) {
						continue;
					}
					const key = `${node.id}->${childId}`;
					if (seen.has(key)) {
						continue;
					}
					seen.add(key);
					links.push(createGraphLink(node.id, childId, '#00ffea'));
				}
			}

			return { nodes: nodes as unknown as Graph['nodes'], links };
		},
	},

	actions: {
		async setEditNode(nodeID: string) {
			console.log('updating editing node');
			const index = findIndex(this.nodes, { id: nodeID });
			if (index !== -1) {
				this.editingGraphNode = this.nodes[index].id;
				// this.editingGraphNode.title = this.nodes[index].title;
				await this.loadLinkedNodes(this.editingGraphNode);
				console.log('updated editing node');
			} else {
				console.log('missing node');
				const gNode = await this.getNodeById(nodeID);
				if (gNode) {
					const node = GraphNodeUtil.graphNodeFromNode(gNode);
					this.addNode(node);
					this.editingGraphNode = node.id;
					await this.loadLinkedNodes(node.id);
				}
			}
			this.onNodeChanged(this.editingGraphNode);
		},
		async setCurrentNode(nodeID: string) {
			const index = findIndex(this.nodes, { id: nodeID });
			if (index !== -1) {
				this.currentGraphNode = nodeID;
				await this.loadLinkedNodes(nodeID);
			} else {
				const gNode = await this.getNodeById(nodeID);
				if (gNode) {
					const node = GraphNodeUtil.graphNodeFromNode(gNode);
					this.addNode(node);
					this.currentGraphNode = node.id;
					await this.loadLinkedNodes(node.id);
				}
			}
			this.editingGraphNode = this.currentGraphNode;
		},
		addNode(node: GNode) {
			const index = findIndex(this.nodes, { id: node.id });
			if (index == -1) {
				this.nodes.push(node);
			} else {
				console.log(`Node ${node.id} already in nodes List`);
			}
		},
		// ─── Graphs (Missions) ────────────────────────────────────────
		async loadGraphs() {
			const { $firestore } = useNuxtApp();
			const graphsSnapshot = await getDocs(collection($firestore as Firestore, 'graphs'));
			const graphs: GraphDoc[] = [];
			graphsSnapshot.forEach((graphDoc) => {
				const data = graphDoc.data() as { name: string; root: string };
				graphs.push({ id: graphDoc.id, name: data.name, root: data.root });
			});
			this.graphs = graphs;

			// Auto-migrate: any parentless node with no graph doc becomes its
			// own Mission (covers pre-graph data and unresolved orphans).
			const nodesRef = collection($firestore as Firestore, 'graphNodes');
			const rootSnapshot = await getDocs(query(nodesRef, where('parents', '==', [])));
			for (const docSnap of rootSnapshot.docs) {
				if (!this.graphs.some((g) => g.root === docSnap.id)) {
					const node = docSnap.data() as GNode;
					await this.createGraphDoc(node.title || 'Untitled Mission', docSnap.id);
				}
			}

			if (this.graphs.length === 0) {
				await this.createGraph('New Mission');
			} else if (!this.currentGraphId) {
				await this.selectGraph(this.graphs[0].id);
			}
			await this.loadGlyphDefaults();
			await this.loadLocations();
			await this.ensureGlyphsForNodes();
			console.log(`Loaded ${this.graphs.length} graph(s)`);
		},
		// ─── Locations (user-editable categories + sub-locations) ──────
		async loadLocations() {
			try {
				const { $firestore } = useNuxtApp();
				const snap = await getDocs(collection($firestore as Firestore, 'locations'));
				const locs: GraphLocation[] = [];
				snap.forEach((d) => {
					const data = d.data() as { name: string; subtypes?: { label: string; value: string }[] };
					locs.push({ id: d.id, name: data.name, subtypes: data.subtypes ?? [] });
				});
				this.locations = locs.sort((a, b) => a.name.localeCompare(b.name));
			} catch (error) {
				console.error('Failed to load locations:', error);
			}
		},
		isLocationType(type: string): boolean {
			return this.locations.some((l) => l.id === type);
		},
		// Sub-location options for a location type (empty if not a location).
		locationSubtypes(type: string): { label: string; value: string }[] {
			return find(this.locations, { id: type })?.subtypes ?? [];
		},
		async addLocation(name: string): Promise<string | null> {
			const trimmed = name.trim();
			if (!trimmed) {
				return null;
			}
			const id = slugify(trimmed);
			if (!id || this.locations.some((l) => l.id === id)) {
				return id || null;
			}
			const { $firestore } = useNuxtApp();
			await setDoc(doc(collection($firestore as Firestore, 'locations'), id), { name: trimmed, subtypes: [] });
			this.locations = [...this.locations, { id, name: trimmed, subtypes: [] }].sort((a, b) => a.name.localeCompare(b.name));
			return id;
		},
		async deleteLocation(id: string) {
			const { $firestore } = useNuxtApp();
			await deleteDoc(doc(collection($firestore as Firestore, 'locations'), id));
			this.locations = this.locations.filter((l) => l.id !== id);
		},
		async addSubLocation(locationId: string, label: string): Promise<string | null> {
			const loc = find(this.locations, { id: locationId });
			const trimmed = label.trim();
			if (!loc || !trimmed) {
				return null;
			}
			const value = slugify(trimmed);
			if (!value || loc.subtypes.some((s) => s.value === value)) {
				return value || null;
			}
			const subtypes = [...loc.subtypes, { label: trimmed, value }];
			const { $firestore } = useNuxtApp();
			await setDoc(doc(collection($firestore as Firestore, 'locations'), locationId), { name: loc.name, subtypes });
			this.locations = this.locations.map((l) => (l.id === locationId ? { ...l, subtypes } : l));
			return value;
		},
		async deleteSubLocation(locationId: string, value: string) {
			const loc = find(this.locations, { id: locationId });
			if (!loc) {
				return;
			}
			const subtypes = loc.subtypes.filter((s) => s.value !== value);
			const { $firestore } = useNuxtApp();
			await setDoc(doc(collection($firestore as Firestore, 'locations'), locationId), { name: loc.name, subtypes });
			this.locations = this.locations.map((l) => (l.id === locationId ? { ...l, subtypes } : l));
		},
		// ─── Glyphs (Itemizer archive) ────────────────────────────────
		async loadGlyphDefaults() {
			try {
				const { $firestore } = useNuxtApp();
				const settingsRef = collection($firestore as Firestore, 'graphSettings');
				const glyphDoc = await getDoc(doc(settingsRef, 'glyphDefaults'));
				this.glyphDefaults = (glyphDoc.data() as Record<string, string>) ?? {};
				const colorDoc = await getDoc(doc(settingsRef, 'colorDefaults'));
				this.colorDefaults = (colorDoc.data() as Record<string, string>) ?? {};
			} catch (error) {
				// Non-fatal: defaults just don't apply (e.g. rules not deployed yet).
				console.error('Failed to load graph defaults:', error);
			}
		},
		// Save the editing node's colour as the default for its type/subtype.
		async saveColorDefault() {
			const node = this.findLocalNode(this.editingGraphNode);
			if (!node || !node.color || !node.type) {
				return;
			}
			const key = node.subType ? `${node.type}:${node.subType}` : node.type;
			const { $firestore } = useNuxtApp();
			await setDoc(
				doc(collection($firestore as Firestore, 'graphSettings'), 'colorDefaults'),
				{ [key]: node.color },
				{ merge: true }
			);
			this.colorDefaults = { ...this.colorDefaults, [key]: node.color };
		},
		// Default colour for a node's type/subtype, if one was saved.
		colorDefaultFor(node: GNode): string {
			return (
				this.colorDefaults[`${node.type}:${node.subType}`] ||
				this.colorDefaults[node.type] ||
				''
			);
		},
		async onGlyphChange(glyphName: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.glyph = glyphName;
				await this.ensureGlyphLoaded(glyphName);
				await this.save();
				this.onNodeChanged(this.editingGraphNode);
			}
		},
		// Separate glyph colour (null/'' = use the node colour).
		onGlyphColorChange(color: string | null) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.glyphColor = color || null;
				this.scheduleSave();
				this.onNodeChanged(this.editingGraphNode);
			}
		},
		// ─── Node reference images (Info > Images, max 5) ─────────────
		addNodeImage() {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node && (node.images?.length ?? 0) < 5) {
				node.images = [...(node.images ?? []), { prompt: '', url: '' }];
				this.save();
			}
		},
		removeNodeImage(index: number) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node && node.images?.[index]) {
				node.images = node.images.filter((_, i) => i !== index);
				this.save();
			}
		},
		setNodeImagePrompt(index: number, prompt: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			const img = node?.images?.[index];
			if (img) {
				img.prompt = prompt;
				this.scheduleSave();
			}
		},
		setNodeImageUrl(index: number, url: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			const img = node?.images?.[index];
			if (img) {
				img.url = url;
				this.save();
			}
		},
		// Save the editing node's glyph as the default for its type/subtype.
		async saveGlyphDefault() {
			const node = this.findLocalNode(this.editingGraphNode);
			if (!node || !node.glyph || !node.type) {
				return;
			}
			const key = node.subType ? `${node.type}:${node.subType}` : node.type;
			const { $firestore } = useNuxtApp();
			await setDoc(
				doc(collection($firestore as Firestore, 'graphSettings'), 'glyphDefaults'),
				{ [key]: node.glyph },
				{ merge: true }
			);
			this.glyphDefaults = { ...this.glyphDefaults, [key]: node.glyph };
		},
		async ensureGlyphLoaded(glyphName: string) {
			if (!glyphName || this.glyphSvgCache[glyphName]) {
				return;
			}
			const text = await fetchSvgText(glyphName);
			if (text) {
				this.glyphSvgCache = { ...this.glyphSvgCache, [glyphName]: text };
			}
		},
		// Load every glyph referenced by loaded nodes (explicit or via defaults).
		async ensureGlyphsForNodes() {
			const needed = new Set<string>();
			for (const node of this.nodes) {
				const name =
					node.glyph ||
					this.glyphDefaults[`${node.type}:${node.subType}`] ||
					this.glyphDefaults[node.type];
				if (name) {
					needed.add(name);
				}
			}
			for (const name of needed) {
				await this.ensureGlyphLoaded(name);
			}
		},
		async selectGraph(graphId: string) {
			const graph = find(this.graphs, { id: graphId });
			if (!graph) {
				return;
			}
			this.currentGraphId = graphId;
			const root = await this.getNodeById(graph.root);
			if (root) {
				await this.setCurrentNode(root.id);
			}
		},
		// Create a Mission: a graph doc plus its root node.
		async createGraph(name: string) {
			const rootNode = GraphNodeUtil.defaultNode();
			rootNode.title = name;
			rootNode.type = 'mission';
			rootNode.shape = type2Shape['mission'];
			rootNode.svgUrl = getType2SVG('mission');
			rootNode.svgData = rootNode.svgUrl ? await loadSvg(rootNode.svgUrl) : '';
			const newNode = await this.upsertNode(rootNode);
			if (!newNode) {
				return;
			}
			const graphDoc = await this.createGraphDoc(name, newNode.id);
			await this.selectGraph(graphDoc.id);
		},
		async createGraphDoc(name: string, rootId: string): Promise<GraphDoc> {
			const { $firestore } = useNuxtApp();
			const graphRef = doc(collection($firestore as Firestore, 'graphs'));
			const graphDoc: GraphDoc = { id: graphRef.id, name, root: rootId };
			await setDoc(graphRef, { name: graphDoc.name, root: graphDoc.root });
			this.graphs = [...this.graphs, graphDoc];
			return graphDoc;
		},
		async renameGraph(graphId: string, name: string) {
			const graph = find(this.graphs, { id: graphId });
			const trimmed = name.trim();
			if (!graph || !trimmed) {
				return;
			}
			const { $firestore } = useNuxtApp();
			await setDoc(doc(collection($firestore as Firestore, 'graphs'), graphId), { name: trimmed, root: graph.root });
			this.graphs = this.graphs.map((g) => (g.id === graphId ? { ...g, name: trimmed } : g));
		},
		// Delete a Mission and every node reachable from its root.
		async deleteGraph(graphId: string) {
			const graph = find(this.graphs, { id: graphId });
			if (!graph) {
				return;
			}
			await this.deleteSubtree(graph.root);
			await this.removeGraphDoc(graphId);
			await this.ensureSelection();
		},
		// Remove just the graph doc (used when a graph is merged via join).
		async removeGraphDoc(graphId: string) {
			const { $firestore } = useNuxtApp();
			await deleteDoc(doc(collection($firestore as Firestore, 'graphs'), graphId));
			this.graphs = this.graphs.filter((g) => g.id !== graphId);
			if (this.currentGraphId === graphId) {
				this.currentGraphId = '';
			}
		},
		// Every node reachable from the graph's root (BFS), for node pickers.
		async listGraphNodes(graphId: string): Promise<GNode[]> {
			const graph = find(this.graphs, { id: graphId });
			if (!graph) {
				return [];
			}
			const visited = new Set<string>();
			const result: GNode[] = [];
			const queue = [graph.root];
			while (queue.length > 0) {
				const id = queue.shift() as string;
				if (visited.has(id)) {
					continue;
				}
				visited.add(id);
				const node = await this.getNodeById(id);
				if (node) {
					result.push(node);
					queue.push(...node.children);
				}
			}
			return result;
		},
		// ─── DOT export / import ──────────────────────────────────────
		// Serialise the current Mission (its reachable node set) to a Graphviz
		// DOT string for download. null if no mission is selected.
		async exportCurrentGraphDot(): Promise<{ name: string; dot: string } | null> {
			const graph = find(this.graphs, { id: this.currentGraphId });
			if (!graph) {
				return null;
			}
			const nodes = await this.listGraphNodes(graph.id);
			return { name: graph.name, dot: graphToDot(graph.name, nodes, graph.root) };
		},
		// Import a DOT string. mode 'new' builds a brand new Mission (every node
		// gets a fresh id, so it never overwrites anything). mode 'merge' folds
		// the DOT into the current Mission: a DOT node whose id matches an
		// existing mission node updates it in place, an unknown id becomes a new
		// node, and edges among DOT-mentioned nodes are reconciled to the DOT.
		// Built for an export -> (AI edits) -> merge round trip.
		async importGraphFromDot(
			text: string,
			mode: 'new' | 'merge' = 'new'
		): Promise<{ ok: boolean; name?: string; count?: number; added?: number; error?: string }> {
			let parsed;
			try {
				parsed = parseDot(text);
			} catch (e) {
				return { ok: false, error: (e as Error).message };
			}
			if (!parsed.nodes.length) {
				return { ok: false, error: 'No nodes found in the DOT file.' };
			}
			if (mode === 'merge') {
				return this.mergeDotIntoCurrent(parsed);
			}

			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'graphNodes');

			// Mint a fresh id for every DOT node up front so edges can wire
			// parents/children by the remapped ids.
			const idMap = new Map<string, string>();
			for (const dn of parsed.nodes) {
				idMap.set(dn.id, doc(nodesRef).id);
			}

			// Materialise nodes with their round-trip fields.
			const built = new Map<string, GNode>();
			for (const dn of parsed.nodes) {
				const node = GraphNodeUtil.defaultNode();
				node.id = idMap.get(dn.id) as string;
				node.parents = [];
				node.children = [];
				applyDotFields(node, dotNodeFields(dn.attrs));
				await this.deriveNodeSvg(node);
				built.set(dn.id, node);
			}

			// Wire edges (deduped) as parent -> child on both sides.
			for (const edge of parsed.edges) {
				const parent = built.get(edge.from);
				const child = built.get(edge.to);
				if (!parent || !child) {
					continue;
				}
				if (!parent.children.includes(child.id)) {
					parent.children.push(child.id);
				}
				if (!child.parents.includes(parent.id)) {
					child.parents.push(parent.id);
				}
			}

			// Pick the root: explicit cc_root, else a mission-typed node, else a
			// parentless node, else the first node.
			const rootDotId =
				parsed.nodes.find((n) => dotNodeFields(n.attrs).isRoot)?.id ||
				parsed.nodes.find((n) => dotNodeFields(n.attrs).type === 'mission')?.id ||
				[...built.entries()].find(([, n]) => n.parents.length === 0)?.[0] ||
				parsed.nodes[0].id;
			const rootId = idMap.get(rootDotId) as string;

			this.captureUndo('Import DOT');
			for (const node of built.values()) {
				await this.ensureGlyphLoaded(node.glyph);
				await this.upsertNode(node);
			}

			// Avoid a duplicate mission name.
			const base = (parsed.name || 'Imported Mission').trim() || 'Imported Mission';
			let name = base;
			for (let i = 2; this.graphs.some((g) => g.name === name); i++) {
				name = `${base} (${i})`;
			}
			const graphDoc = await this.createGraphDoc(name, rootId);
			await this.selectGraph(graphDoc.id);
			return { ok: true, name, count: built.size, added: built.size };
		},
		// Merge a parsed DOT into the currently selected Mission. Matched nodes
		// (by id) are updated with the DOT's non-empty fields; unknown ids are
		// added as new nodes; edges between DOT-mentioned nodes are reconciled to
		// the DOT (missing links added, absent links dropped). Links and nodes the
		// DOT never mentions are left untouched, and the mission root is unchanged.
		async mergeDotIntoCurrent(
			parsed: ParsedDot
		): Promise<{ ok: boolean; name?: string; count?: number; added?: number; error?: string }> {
			const graph = find(this.graphs, { id: this.currentGraphId });
			if (!graph) {
				return { ok: false, error: 'Select a mission to merge into.' };
			}
			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'graphNodes');

			// Nodes currently in the mission, indexed by id for matching.
			const existing = await this.listGraphNodes(graph.id);
			const existingById = new Map(existing.map((n) => [n.id, n]));

			// Remap DOT ids: a match keeps its id; an unknown id gets a fresh one.
			const idMap = new Map<string, string>();
			let added = 0;
			for (const dn of parsed.nodes) {
				if (existingById.has(dn.id)) {
					idMap.set(dn.id, dn.id);
				} else {
					idMap.set(dn.id, doc(nodesRef).id);
					added++;
				}
			}

			// Working copies (existing clone with fresh arrays, or a new node).
			const working = new Map<string, GNode>();
			for (const dn of parsed.nodes) {
				const realId = idMap.get(dn.id) as string;
				const prev = existingById.get(dn.id);
				const node: GNode = prev
					? {
							...prev,
							parents: [...(prev.parents ?? [])],
							children: [...(prev.children ?? [])],
							tags: [...(prev.tags ?? [])],
							images: [...(prev.images ?? [])],
						}
					: GraphNodeUtil.defaultNode();
				if (!prev) {
					node.id = realId;
					node.parents = [];
					node.children = [];
				}
				const beforeType = node.type;
				applyDotFields(node, dotNodeFields(dn.attrs));
				// Re-derive the icon for brand new nodes or a changed type.
				if (!prev || node.type !== beforeType) {
					await this.deriveNodeSvg(node);
				}
				working.set(realId, node);
			}

			const mentioned = new Set(working.keys());

			// Desired parent -> child links among mentioned nodes.
			const desired = new Set<string>();
			for (const edge of parsed.edges) {
				const p = idMap.get(edge.from);
				const c = idMap.get(edge.to);
				if (p && c && working.has(p) && working.has(c)) {
					desired.add(`${p}->${c}`);
				}
			}

			// Reconcile edges: among mentioned nodes the DOT is authoritative (add
			// the missing, drop the absent); links touching a node the DOT never
			// mentions are preserved.
			for (const node of working.values()) {
				node.children = node.children.filter(
					(c) => !mentioned.has(c) || desired.has(`${node.id}->${c}`)
				);
				node.parents = node.parents.filter(
					(p) => !mentioned.has(p) || desired.has(`${p}->${node.id}`)
				);
			}
			for (const key of desired) {
				const [p, c] = key.split('->');
				const parent = working.get(p) as GNode;
				const child = working.get(c) as GNode;
				if (!parent.children.includes(c)) {
					parent.children.push(c);
				}
				if (!child.parents.includes(p)) {
					child.parents.push(p);
				}
			}

			this.captureUndo('Merge DOT');
			for (const node of working.values()) {
				await this.ensureGlyphLoaded(node.glyph);
				await this.upsertNode(node);
			}
			await this.selectGraph(graph.id);
			return { ok: true, name: graph.name, count: working.size, added };
		},
		// Set a node's type icon (location types share the generic icon).
		async deriveNodeSvg(node: GNode) {
			const svgUrl = this.isLocationType(node.type)
				? '/svg/graph/location.svg'
				: getType2SVG(node.type);
			node.svgUrl = svgUrl;
			node.svgData = svgUrl ? await loadSvg(svgUrl) : '';
		},
		// ─── Map view (full graph) ────────────────────────────────────
		setViewMode(mode: 'focus' | 'map') {
			this.viewMode = mode;
		},
		// BFS-load every node reachable from the current graph's root into
		// this.nodes so getMapGraph can build the full graph from local state.
		async loadFullGraph() {
			if (!this.currentGraphId) {
				return;
			}
			await this.listGraphNodes(this.currentGraphId);
			await this.ensureGlyphsForNodes();
		},
		// Persist a node's Map-mode layout position. Only called from Map mode.
		async setNodePosition(nodeId: string, x: number, y: number) {
			const node = this.findLocalNode(nodeId);
			if (!node) {
				return;
			}
			// Avoid exactly 0, which graphly's spawnNodes treats as "unplaced"
			// (it tests x/fx truthiness) and would re-spawn the node.
			node.x = x === 0 ? 0.0001 : x;
			node.y = y === 0 ? 0.0001 : y;
			await this.upsertNode(node);
		},
		// Make parentId a parent of childId, persisted on both sides.
		// ─── Undo (single level, structural actions) ──────────────────
		// Snapshot nodes + graphs before a mutating action so it can be undone.
		captureUndo(label: string) {
			this.undoSnapshot = {
				nodes: JSON.parse(JSON.stringify(this.nodes)) as GNode[],
				graphs: JSON.parse(JSON.stringify(this.graphs)) as GraphDoc[],
				label,
			};
		},
		async undo() {
			const snap = this.undoSnapshot;
			if (!snap) {
				return;
			}
			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'graphNodes');
			const graphsRef = collection($firestore as Firestore, 'graphs');

			// Restore every snapshot node; delete nodes created since the snapshot.
			const snapNodeIds = new Set(snap.nodes.map((n) => n.id));
			for (const n of snap.nodes) {
				await setDoc(doc(nodesRef, n.id), n);
			}
			for (const cur of this.nodes) {
				if (!snapNodeIds.has(cur.id)) {
					await deleteDoc(doc(nodesRef, cur.id));
				}
			}

			// Same for graph docs.
			const snapGraphIds = new Set(snap.graphs.map((g) => g.id));
			for (const g of snap.graphs) {
				await setDoc(doc(graphsRef, g.id), { name: g.name, root: g.root });
			}
			for (const cur of this.graphs) {
				if (!snapGraphIds.has(cur.id)) {
					await deleteDoc(doc(graphsRef, cur.id));
				}
			}

			this.nodes = snap.nodes.map((n) => ({ ...n }));
			this.graphs = snap.graphs.map((g) => ({ ...g }));
			this.pendingOrphans = [];
			this.undoSnapshot = null;
			await this.ensureSelection();
		},
		// True if `targetId` is reachable from `startId` via children, following
		// edges in the live graph (loads descendants on demand). Optionally skip
		// the direct start->skipChildId edge (used by flip to test other paths).
		async reachesViaChildren(startId: string, targetId: string, skipChildId?: string): Promise<boolean> {
			const seen = new Set<string>();
			const startNode = await this.getNodeById(startId);
			const queue = (startNode?.children ?? []).filter((c) => c !== skipChildId);
			while (queue.length > 0) {
				const id = queue.shift() as string;
				if (seen.has(id)) {
					continue;
				}
				seen.add(id);
				if (id === targetId) {
					return true;
				}
				const node = await this.getNodeById(id);
				queue.push(...(node?.children ?? []));
			}
			return false;
		},
		// Adding parentId -> childId creates a cycle iff parent IS child, or the
		// child can already reach the parent via children (child ->* parent).
		async linkWouldCycle(parentId: string, childId: string): Promise<boolean> {
			if (parentId === childId) {
				return true;
			}
			return this.reachesViaChildren(childId, parentId);
		},
		async linkNodes(parentId: string, childId: string): Promise<boolean> {
			if (parentId === childId) {
				return false;
			}
			if (await this.linkWouldCycle(parentId, childId)) {
				console.warn(`Refused link ${parentId} -> ${childId}: would create a cycle.`);
				return false;
			}
			const parent = await this.getNodeById(parentId);
			const child = await this.getNodeById(childId);
			if (!parent || !child) {
				return false;
			}
			if (!parent.children.includes(childId)) {
				parent.children = [...parent.children, childId];
				await this.upsertNode(parent);
			}
			if (!child.parents.includes(parentId)) {
				child.parents = [...child.parents, parentId];
				await this.upsertNode(child);
			}
			return true;
		},
		// Join the current graph into another: the target node becomes the new
		// parent of this graph's root, and the merged graph doc goes away.
		async joinCurrentGraphTo(targetGraphId: string, targetNodeId: string): Promise<boolean> {
			const source = find(this.graphs, { id: this.currentGraphId });
			if (!source || source.id === targetGraphId) {
				return false;
			}
			if (await this.linkWouldCycle(targetNodeId, source.root)) {
				console.warn('Refused mission join: would create a cycle.');
				return false;
			}
			this.captureUndo('Join mission');
			await this.linkNodes(targetNodeId, source.root);
			await this.removeGraphDoc(source.id);
			await this.selectGraph(targetGraphId);
			await this.setCurrentNode(targetNodeId);
			return true;
		},
		// Delete a node and, recursively, any children left with no other parent.
		async deleteSubtree(nodeId: string, visited: Set<string> = new Set()) {
			if (visited.has(nodeId)) {
				return;
			}
			visited.add(nodeId);
			const node = await this.getNodeById(nodeId);
			if (!node) {
				return;
			}
			for (const childId of [...node.children]) {
				const child = await this.getNodeById(childId);
				if (!child) {
					continue;
				}
				const remaining = child.parents.filter((id) => id !== nodeId);
				if (remaining.length === 0) {
					await this.deleteSubtree(childId, visited);
				} else {
					child.parents = remaining;
					await this.upsertNode(child);
				}
			}
			for (const parentId of node.parents) {
				const pNode = await this.getNodeById(parentId);
				if (pNode) {
					pNode.children = pNode.children.filter((id) => id !== nodeId);
					await this.upsertNode(pNode);
				}
			}
			const { $firestore } = useNuxtApp();
			await deleteDoc(doc(collection($firestore as Firestore, 'graphNodes'), nodeId));
			const index = findIndex(this.nodes, { id: nodeId });
			if (index !== -1) {
				this.nodes.splice(index, 1);
			}
		},
		// ─── Orphan resolution (Join / Delete / As new graph) ─────────
		openJoinDialog(mode: 'join' | 'orphan' | 'node' | 'setparent' | 'flip' | 'unjoin', subjectNodeId = '') {
			this.joinDialog = { open: true, mode, subjectNodeId };
		},
		closeJoinDialog() {
			this.joinDialog = { ...this.joinDialog, open: false };
		},
		consumeOrphan(orphanRootId: string) {
			this.pendingOrphans = this.pendingOrphans.filter((id) => id !== orphanRootId);
		},
		// Link a node under another node (cross-link within / across trees).
		async resolveNodeJoin(nodeId: string, targetNodeId: string): Promise<boolean> {
			if (await this.linkWouldCycle(targetNodeId, nodeId)) {
				console.warn(`Refused join ${nodeId} under ${targetNodeId}: would create a cycle.`);
				return false;
			}
			this.captureUndo('Join node');
			const ok = await this.linkNodes(targetNodeId, nodeId);
			await this.setCurrentNode(nodeId);
			return ok;
		},
		// Reassign a node's parent: detach from all current parents, then link
		// the picked node as its sole parent.
		async resolveNodeReparent(nodeId: string, newParentId: string): Promise<boolean> {
			if (nodeId === newParentId) {
				return false;
			}
			// Check the cycle BEFORE detaching, so a refusal leaves the node intact.
			if (await this.linkWouldCycle(newParentId, nodeId)) {
				console.warn(`Refused reparent ${nodeId} under ${newParentId}: would create a cycle.`);
				return false;
			}
			const node = await this.getNodeById(nodeId);
			if (!node) {
				return false;
			}
			this.captureUndo('Set parent');
			for (const parentId of [...node.parents]) {
				const parent = await this.getNodeById(parentId);
				if (parent) {
					parent.children = parent.children.filter((id) => id !== nodeId);
					await this.upsertNode(parent);
				}
			}
			node.parents = [];
			// If the new parent was a child of this node, break that inverted
			// edge so we don't create a 2-cycle (which would dupe the node in
			// the focus view).
			if (node.children.includes(newParentId)) {
				node.children = node.children.filter((id) => id !== newParentId);
				const np = await this.getNodeById(newParentId);
				if (np) {
					np.parents = np.parents.filter((id) => id !== nodeId);
					await this.upsertNode(np);
				}
			}
			await this.upsertNode(node);
			await this.linkNodes(newParentId, nodeId);
			await this.setCurrentNode(nodeId);
			return true;
		},
		// Reverse the directed edge between two connected nodes (parent<->child).
		// Refuses if reversing would create a cycle via some other path.
		async flipEdge(nodeId: string, otherId: string): Promise<boolean> {
			if (nodeId === otherId) {
				return false;
			}
			const node = await this.getNodeById(nodeId);
			const other = await this.getNodeById(otherId);
			if (!node || !other) {
				return false;
			}
			// Identify the current edge direction P -> C; the flip makes C -> P.
			let parentId: string;
			let childId: string;
			if (node.parents.includes(otherId)) {
				parentId = otherId;
				childId = nodeId;
			} else if (node.children.includes(otherId)) {
				parentId = nodeId;
				childId = otherId;
			} else {
				return false; // not connected
			}
			// After flip the new edge is child -> parent; that cycles iff the
			// parent can still reach the child by another path (ignoring the
			// edge we're removing).
			if (await this.reachesViaChildren(parentId, childId, childId)) {
				console.warn(`Refused flip ${parentId} -> ${childId}: would create a cycle.`);
				return false;
			}
			this.captureUndo('Flip arrow');
			const p = await this.getNodeById(parentId);
			const c = await this.getNodeById(childId);
			if (!p || !c) {
				return false;
			}
			p.children = p.children.filter((id) => id !== childId);
			c.parents = c.parents.filter((id) => id !== parentId);
			c.children = [...new Set([...c.children, parentId])];
			p.parents = [...new Set([...p.parents, childId])];
			await this.upsertNode(p);
			await this.upsertNode(c);
			await this.setCurrentNode(nodeId);
			return true;
		},
		// Remove the directed edge between two connected nodes. If the child
		// side is left parentless (and isn't the graph root), it becomes an
		// orphan and is queued for the Join/Delete/New-graph dialog.
		async unjoinEdge(nodeId: string, otherId: string) {
			if (nodeId === otherId) {
				return;
			}
			const node = await this.getNodeById(nodeId);
			const other = await this.getNodeById(otherId);
			if (!node || !other) {
				return;
			}
			this.captureUndo('Unjoin');
			let childId: string | null = null;
			if (node.parents.includes(otherId)) {
				other.children = other.children.filter((id) => id !== nodeId);
				node.parents = node.parents.filter((id) => id !== otherId);
				childId = nodeId;
			} else if (node.children.includes(otherId)) {
				node.children = node.children.filter((id) => id !== otherId);
				other.parents = other.parents.filter((id) => id !== nodeId);
				childId = otherId;
			} else {
				return; // not connected
			}
			await this.upsertNode(node);
			await this.upsertNode(other);
			await this.setCurrentNode(nodeId);

			const child = this.findLocalNode(childId);
			const graph = find(this.graphs, { id: this.currentGraphId });
			if (child && child.parents.length === 0 && graph?.root !== childId) {
				this.pendingOrphans = [...this.pendingOrphans, childId];
			}
		},
		async resolveOrphanJoin(orphanRootId: string, targetGraphId: string, targetNodeId: string): Promise<boolean> {
			if (await this.linkWouldCycle(targetNodeId, orphanRootId)) {
				console.warn('Refused orphan join: would create a cycle.');
				return false;
			}
			await this.linkNodes(targetNodeId, orphanRootId);
			this.consumeOrphan(orphanRootId);
			await this.selectGraph(targetGraphId);
			await this.setCurrentNode(targetNodeId);
			return true;
		},
		async resolveOrphanDelete(orphanRootId: string) {
			await this.deleteSubtree(orphanRootId);
			this.consumeOrphan(orphanRootId);
			await this.ensureSelection();
		},
		async resolveOrphanNewGraph(orphanRootId: string, name: string) {
			const node = await this.getNodeById(orphanRootId);
			const graphDoc = await this.createGraphDoc(name.trim() || node?.title || 'Untitled Mission', orphanRootId);
			this.consumeOrphan(orphanRootId);
			await this.selectGraph(graphDoc.id);
		},
		// Make sure something valid is selected after deletes.
		async ensureSelection() {
			const graphValid = this.currentGraphId && find(this.graphs, { id: this.currentGraphId });
			if (graphValid && this.findLocalNode(this.currentGraphNode)) {
				return;
			}
			if (this.graphs.length > 0) {
				await this.selectGraph(this.graphs[0].id);
			} else {
				await this.createGraph('New Mission');
			}
		},
		async loadLinkedNodes(nodeID: string) {
			const node = await this.getNodeById(nodeID);
			if (node != undefined) {
				for (const nodeId of node.parents) {
					const nodeListIndex = findIndex(this.nodes, { id: nodeId });
					if (nodeListIndex == -1) {
						const pNode = (await this.getNodeById(nodeId)) as GNode | undefined;
						if (!isNil(pNode)) {
							const foundNode = GraphNodeUtil.graphNodeFromNode(pNode);
							this.addNode(foundNode);
						} else {
							console.error(`Unable to locate NodeID ${nodeId}`);
						}
					}
				}

				for (const nodeId of node.children) {
					const nodeListIndex = findIndex(this.nodes, { id: nodeId });
					if (nodeListIndex == -1) {
						const cNode = (await this.getNodeById(nodeId)) as GNode | undefined;
						if (!isNil(cNode)) {
							const foundNode = GraphNodeUtil.graphNodeFromNode(cNode);
							this.addNode(foundNode);
						} else {
							console.error(`Unable to locate NodeID ${nodeId}`);
						}
					}
				}
			}
		},
		findLocalNode(id: string): GNode | undefined  {
			return find(this.nodes, { id });
		},
		async getNodeById(nodeId: string): Promise<GNode | undefined> {
			try {
				const node = this.findLocalNode(nodeId);
				if (isNil(node)) {
					console.log(`Searching FB for ${nodeId}`);
					const { $firestore } = useNuxtApp();
					const docRef = doc($firestore as Firestore, '/graphNodes', nodeId);
					const node = await getDoc(docRef);

					if (!isNil(node.data())) {
						// Normalise through graphNodeFromNode so docs written
						// before newer fields existed get defaults, not undefined.
						const gNode = GraphNodeUtil.graphNodeFromNode(node.data() as GNode);
						this.addNode(gNode);
						return gNode;
					} else {
						console.error(`Node ${nodeId} not found in FB.`);
					}
				}
				return node;
			} catch (error) {
				console.error(`Error fetching node ${nodeId}:`, error);
				// Handle the error as needed
			}
			return undefined;
		},
		async save() {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				await this.upsertNode(node);
				return node;
			}
		},
		// Autosave for typed fields: persists the editing node one second
		// after the last keystroke. If the user switches to another node
		// while a save is pending, the pending node is flushed immediately.
		scheduleSave() {
			const nodeId = this.editingGraphNode;
			if (saveTimer) {
				clearTimeout(saveTimer);
				if (pendingSaveId && pendingSaveId !== nodeId) {
					const previous = this.findLocalNode(pendingSaveId);
					if (previous) {
						this.upsertNode(previous);
					}
				}
			}
			pendingSaveId = nodeId;
			saveTimer = setTimeout(async () => {
				saveTimer = null;
				pendingSaveId = '';
				const node = this.findLocalNode(nodeId);
				if (node) {
					await this.upsertNode(node);
				}
			}, 1000);
		},
		async deleteNode() {
			await this.removeNode(this.editingGraphNode);
		},
		async removeNode(nodeId: string) {
			// TODO : should implement soft deletes
			console.log(`GraphNodeStore RemoveNode: ${nodeId}`);
			if (nodeId === '-1') {
				return;
			}
			const node: GNode | undefined = await this.getNodeById(nodeId);
			if (!node) {
				return;
			}
			await this.loadLinkedNodes(nodeId);
			this.captureUndo('Delete node');
			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'graphNodes');
			await deleteDoc(doc(nodesRef, node.id));

			// Unlink the deleted node from both sides of every edge.
			for (const parentId of node.parents) {
				const pNode = await this.getNodeById(parentId);
				if (pNode) {
					pNode.children = pNode.children.filter((id) => id !== node.id);
					await this.upsertNode(pNode);
				} else {
					console.error(`Unable to find parent ${parentId} listed on ${node.title} ${node.id}`);
				}
			}

			for (const childId of node.children) {
				const cNode = await this.getNodeById(childId);
				if (cNode) {
					cNode.parents = cNode.parents.filter((id) => id !== node.id);
					await this.upsertNode(cNode);
				} else {
					console.error(`Unable to find child ${childId} listed on ${node.title} ${node.id}`);
				}
			}

			// Drop it from local state so the graph stops drawing it.
			const index = findIndex(this.nodes, { id: node.id });
			if (index !== -1) {
				this.nodes.splice(index, 1);
			}

			// Children left with no parents head an orphaned subtree; queue
			// them for the Join / Delete / As-new-graph dialog.
			const orphans = node.children.filter((childId) => {
				const child = this.findLocalNode(childId);
				return child != null && child.parents.length === 0;
			});

			// If the graph's root was deleted, its graph doc is dangling.
			const graph = find(this.graphs, { id: this.currentGraphId });
			if (graph && graph.root === node.id) {
				await this.removeGraphDoc(graph.id);
			}

			// Re-centre on the deleted node's parent; orphan resolution (or
			// ensureSelection) picks the spot otherwise.
			if (node.parents.length > 0) {
				await this.setCurrentNode(node.parents[0]);
			} else if (node.children.length > 0 && orphans.length === 0) {
				await this.setCurrentNode(node.children[0]);
			}

			if (orphans.length > 0) {
				this.pendingOrphans = [...this.pendingOrphans, ...orphans];
			} else if (node.parents.length === 0 && node.children.length === 0) {
				await this.ensureSelection();
			}
		},
		async upsertNode(node: GNode) {
			console.log(`GraphNodeStore upsertNode: ${node.id}`);
			const { $firestore } = useNuxtApp();
			const nodesRef = collection($firestore as Firestore, 'graphNodes');
			let nodeRef = doc(nodesRef);
			if (node.id == '-1') {
				node.id = nodeRef.id;
				console.log(`noderef.id ${nodeRef.id}`);
			} else {
				console.log(`item.id ${node.id}`);
				nodeRef = doc(nodesRef, node.id);
			}

			return await setDoc(nodeRef, node)
				.then(() => {
					this.updateNode(node);
					return node;
				})
				.catch((error) => {
					console.log('Error saving Node: ', error);
				});
		},
		updateNode(item: GNode) {
			const index = findIndex(this.nodes, { id: item.id });
			if (index == -1) {
				this.addNode(item);
			} else {
				// Replace in place; reordering (old splice+push) churned the
				// array and re-rendered the graph on every autosave.
				this.nodes[index] = item;
			}
		},
		async addChild() {
			await this.save();
			const node = this.findLocalNode(this.currentGraphNode);
			if (node) {
				this.captureUndo('Add child');
				console.log('GraphNodeStore addChild:');
				const childNode = GraphNodeUtil.defaultNode();
				childNode.parents.push(node.id);
				const newNode = await this.upsertNode(childNode);
				if (newNode) {
					console.log(`newNode.id ${newNode.id}`);
					node.children.push(newNode.id);
					await this.upsertNode(node);
					this.loadLinkedNodes(this.currentGraphNode);
					this.setEditNode(newNode.id);
				}
			}
		},
		async addParent() {
			await this.save();
			const node = this.findLocalNode(this.currentGraphNode);
			if (node) {
				this.captureUndo('Add parent');
				console.log('GraphNodeStore Add Parent:');
				const parentNode = GraphNodeUtil.defaultNode();
				parentNode.children.push(node.id);
				const newNode = await this.upsertNode(parentNode);
				if (newNode) {
					console.log(`newNode.id ${newNode.id}`);
					node.parents.push(newNode.id);
					await this.upsertNode(node);
					this.loadLinkedNodes(this.currentGraphNode);
					this.setEditNode(newNode.id);
				}
			}
		},
		// Swap the editing node with its neighbour in the current (centre)
		// node's draw order. Parents and children are positioned along their
		// arcs in array order, so swapping array entries swaps their drawn
		// positions. offset: -1 = up (earlier), +1 = down (later).
		async moveEditingNode(offset: number) {
			const center = this.findLocalNode(this.currentGraphNode);
			if (!center || this.editingGraphNode === center.id) {
				return;
			}
			for (const key of ['children', 'parents'] as const) {
				const index = center[key].indexOf(this.editingGraphNode);
				if (index === -1) {
					continue;
				}
				const target = index + offset;
				if (target < 0 || target >= center[key].length) {
					return;
				}
				const reordered = [...center[key]];
				[reordered[index], reordered[target]] = [reordered[target], reordered[index]];
				center[key] = reordered;
				await this.upsertNode(center);
				return;
			}
		},
		async onTitleChange(newTitle: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.title = newTitle;
				this.scheduleSave();
			}
			this.onNodeChanged(this.editingGraphNode);
		},
		onIdentChange(newIdent: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.ident = newIdent;
				this.scheduleSave();
			}
			this.onNodeChanged(this.editingGraphNode);
		},
		onColorChange(newColor: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.color = newColor;
				// The colour picker emits continuously while dragging; the
				// debounce coalesces it into one write.
				this.scheduleSave();
			}
			this.onNodeChanged(this.editingGraphNode);
		},
		async onTagsChange(newTags: string[] | null) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.tags = newTags ?? [];
				await this.save();
			}
			this.onNodeChanged(this.editingGraphNode);
		},
		async onTypeChange(newType: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.type = newType;
				// Default the shape from the type family (location types default
				// to a square); the user can still override it afterwards.
				const shape = type2Shape[newType] ?? (this.isLocationType(newType) ? LOCATION_DEFAULT_SHAPE : undefined);
				if (shape) {
					node.shape = shape;
					const offset = shape2Offset[shape];
					if (offset) {
						node.textX = offset.x;
						node.textY = offset.y;
					}
				}
				const colorDefault = this.colorDefaultFor(node);
				if (colorDefault) {
					node.color = colorDefault;
				}
				await this.updateSvgUrlAndData();
				await this.save();
				this.onNodeChanged(this.editingGraphNode);
			}
		},
		// Info tab fields (GM Info / Read Aloud / Environment); autosaved on
		// the typing debounce.
		onInfoChange(field: 'info' | 'readAloud' | 'environment', value: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node[field] = value;
				this.scheduleSave();
			}
		},
		async onSubTypeChange(newSubtype: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.subType = newSubtype;
				const colorDefault = this.colorDefaultFor(node);
				if (colorDefault) {
					node.color = colorDefault;
				}
				await this.save();
				this.onNodeChanged(this.editingGraphNode);
			}
		},
		async onShapeChange(newShape: string) {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				node.shape = newShape;
				const offset = shape2Offset[newShape];
				if (offset) {
					node.textX = offset.x;
					node.textY = offset.y;
				}
				await this.save();
				this.onNodeChanged(this.editingGraphNode);
			}
		},
		// The icon is type-driven: it renders as a badge in the bottom-right
		// corner of whatever shape the node uses.
		async updateSvgUrlAndData() {
			const node = this.findLocalNode(this.editingGraphNode);
			if (node) {
				// Location categories share the generic location icon as their
				// type fallback (the per-node glyph still overrides it).
				const svgUrl = this.isLocationType(node.type) ? '/svg/graph/location.svg' : getType2SVG(node.type);
				node.svgUrl = svgUrl;
				node.svgData = svgUrl ? await loadSvg(svgUrl) : '';
			}
		},
		onNodeChanged(changedNodeId: string) {
			// rebuildGraph();
		},
	},
});

// Hot-swap store actions/getters on dev edits; without this the live store
// instance keeps the old actions until a full page reload.
if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useGraphNodeStore, import.meta.hot));
}
